import {
  ComboboxItem,
  Loader,
  OptionsFilter,
  Select,
  SelectProps,
} from "@mantine/core";
import {
  DefaultError,
  DefinedInitialDataInfiniteOptions,
  GetNextPageParamFunction,
  GetPreviousPageParamFunction,
  InfiniteData,
  QueryKey,
  useInfiniteQuery,
} from "@tanstack/react-query";

type PaginatedQuery<Data = unknown> = {
  page_size: number;
  current_page: number;
  last_page: number;
  total: number;
  next_page_url?: string | null;
  prev_page_url?: string | null;
} & Data;

interface InfiniteScrollSelectProps<
  TQueryFnData extends PaginatedQuery,
  TPageParam extends number,
  TError = DefaultError,
  TData = InfiniteData<TQueryFnData>,
  TQueryKey extends QueryKey = QueryKey
> extends Omit<SelectProps, "data">,
    DefinedInitialDataInfiniteOptions<
      TQueryFnData,
      TError,
      TData,
      TQueryKey,
      TPageParam
    > {}

const optionsFilter: OptionsFilter = ({ options, search }) => {
  const filtered = (options as ComboboxItem[]).filter((option) =>
    option.label.toLowerCase().trim().includes(search.toLowerCase().trim())
  );

  filtered.sort((a, b) => a.label.localeCompare(b.label));
  return filtered;
};

export function InfiniteScrollSelect<
  TQueryFnData extends PaginatedQuery,
  TPageParam extends number,
  TError = DefaultError,
  TData = InfiniteData<TQueryFnData, TPageParam>,
  TQueryKey extends QueryKey = QueryKey
>({
  scrollAreaProps,
  ...props
}: InfiniteScrollSelectProps<
  TQueryFnData,
  TPageParam,
  TError,
  TData,
  TQueryKey
>) {
  const {
    _defaulted,
    _optimisticResults,
    behavior,
    enabled,
    gcTime,
    getPreviousPageParam,
    getNextPageParam,
    initialDataUpdatedAt,
    initialPageParam,
    maxPages,
    meta,
    networkMode,
    notifyOnChangeProps,
    persister,
    placeholderData,
    queryHash,
    queryKey,
    queryKeyHashFn,
    queryFn,
    refetchInterval,
    refetchIntervalInBackground,
    refetchOnMount,
    refetchOnReconnect,
    refetchOnWindowFocus,
    retry,
    retryDelay,
    retryOnMount,
    initialData,
    select,
    staleTime,
    structuralSharing,
    throwOnError,
    ...selectProps
  } = props;

  const handleGetPreviousPageParam: GetPreviousPageParamFunction<
    TPageParam,
    TQueryFnData
  > = ({ current_page }) => {
    if (current_page > 1) return (current_page - 1) as TPageParam;
    return 1 as TPageParam;
  };

  const handleGetNextPageParam: GetNextPageParamFunction<
    TPageParam,
    TQueryFnData
  > = ({ current_page, last_page }) => {
    if (current_page !== last_page) return (current_page + 1) as TPageParam;
    return last_page as TPageParam;
  };

  const { data, fetchNextPage, isFetchingNextPage } = useInfiniteQuery({
    _defaulted,
    _optimisticResults,
    behavior,
    enabled,
    gcTime,
    getPreviousPageParam: handleGetPreviousPageParam,
    getNextPageParam: handleGetNextPageParam,
    initialDataUpdatedAt,
    initialPageParam,
    maxPages,
    meta,
    networkMode,
    notifyOnChangeProps,
    persister,
    placeholderData,
    queryHash,
    queryKey,
    queryKeyHashFn,
    queryFn,
    refetchInterval,
    refetchIntervalInBackground,
    refetchOnMount,
    refetchOnReconnect,
    refetchOnWindowFocus,
    retry,
    retryDelay,
    retryOnMount,
    initialData,
    select,
    staleTime,
    structuralSharing,
    throwOnError,
  });

  function onBottomReached() {
    console.log("bottom reached");
  }

  const nothingFoundMessage = isFetchingNextPage && <Loader size="sm" />;

  return (
    <Select
      withScrollArea
      filter={optionsFilter}
      maxDropdownHeight={200}
      nothingFoundMessage={nothingFoundMessage}
      data={[]}
      scrollAreaProps={{
        onBottomReached,
        ...scrollAreaProps,
      }}
      {...selectProps}
    />
  );
}

function createSelectOptions(data: PaginatedQuery) {}

// data?.pages.flat().map((subject) => {
//   return {
//     label: subject.name,
//     value: subject.code,
//   }
// });
